/**
 * Cliente del API (patrón de SGDEA): la sesión es una cookie httpOnly; cada petición manda el tenant y los segundos de
 * inactividad. Los errores llegan como ApiError {status, code, message, details}.
 */
import { IDLE_HEADER } from '@planning/contracts';
import { ENTITY_SLUG } from '@xiltrion/identidad';
import { idleSeconds } from '@/app/activity';

let unauthorized: ((code: string) => void) | null = null;
export function onUnauthorized(fn: (code: string) => void) {
  unauthorized = fn;
}
/**
 * Un 401 manda a ingresar **una** vez por página (GUIA §3.1 «Varias pestañas»): las demás peticiones que fallen después
 * no vuelven a redirigir. Mientras se cierra la sesión («Salir»), ninguno.
 */
let unauthorizedSent = false;
let leaving = false;
/** Marca que la página está cerrando la sesión (o, con `false`, que no pudo y sigue). */
export function markLeaving(value = true) {
  leaving = value;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly details?: unknown, public readonly correlationId?: string) {
    super(message);
  }
}

const STORED_TENANT = 'planning.tenant';
const validSlug = (slug: string | null | undefined): string => (slug && ENTITY_SLUG.test(slug) ? slug : '');

/**
 * Entidad (tenant), igual en todos los entornos: `?tenant=<slug>` (el enlace del portal de la suite y la vuelta del
 * ingreso único) → la guardada en este navegador → `VITE_TENANT` de la compilación. `?tenant=` vacío olvida la
 * guardada; uno inválido no se guarda ni cae en otra. Sin ninguna queda vacía: se entra desde el portal de la suite.
 */
function resolveTenant(): string {
  const built = validSlug(import.meta.env.VITE_TENANT?.trim());
  const asked = new URLSearchParams(window.location.search).get('tenant')?.trim();
  if (asked && !validSlug(asked)) return '';
  try {
    if (asked) localStorage.setItem(STORED_TENANT, asked);
    else if (asked === '') localStorage.removeItem(STORED_TENANT);
    else return validSlug(localStorage.getItem(STORED_TENANT)) || built;
  } catch {
    /* sin almacenamiento: vale la de la URL o la compilada */
  }
  return asked || built;
}
export let TENANT = resolveTenant();

/** Sin entidad en la URL ni guardada, la de la sesión vigente (la cookie la trae) pasa a ser la del navegador. */
export function adoptTenant(slug: string) {
  if (TENANT || !validSlug(slug)) return;
  TENANT = slug;
  try {
    localStorage.setItem(STORED_TENANT, slug);
  } catch {
    /* sin almacenamiento: vale para esta pestaña */
  }
}
/** Olvida la entidad de este navegador (no existe en Planning): el ingreso sigue sin ella y la suite decide. */
export function forgetTenant() {
  TENANT = '';
  try {
    localStorage.removeItem(STORED_TENANT);
  } catch {
    /* sin almacenamiento */
  }
}
export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

export async function api<T>(path: string, init: RequestInit & { json?: unknown; query?: Query } = {}): Promise<T> {
  const { json, query, headers, ...rest } = init;
  let url = `${API_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) v.forEach((x) => qs.append(k, x));
      else qs.set(k, String(v));
    }
    const q = qs.toString();
    if (q) url += `?${q}`;
  }
  const res = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: { ...(TENANT ? { 'x-tenant-id': TENANT } : {}), [IDLE_HEADER]: String(idleSeconds()), ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...(headers ?? {}) },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (data ?? {}) as { code?: string; message?: string; details?: unknown; correlationId?: string };
    if (res.status === 401 && !path.startsWith('/auth/') && !leaving && !unauthorizedSent) {
      unauthorizedSent = true;
      unauthorized?.(e.code ?? 'UNAUTHENTICATED');
    }
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? `Error ${res.status}`, e.details, e.correlationId);
  }
  return data as T;
}

export const apiGet = <T>(path: string, query?: Query) => api<T>(path, { query });
export const apiPost = <T>(path: string, json?: unknown) => api<T>(path, { method: 'POST', json });
export const apiPut = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PUT', json });
export const apiPatch = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PATCH', json });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** Sube archivos (multipart). */
export async function apiUpload<T>(path: string, files: File[], fields: Record<string, string> = {}): Promise<T> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) fd.append(k, v);
  for (const f of files) fd.append('file', f, f.name);
  return api<T>(path, { method: 'POST', body: fd });
}

/** URL de descarga con el tenant (para <a href>). */
export const downloadUrl = (path: string, query: Query = {}) => {
  const qs = new URLSearchParams({ ...(TENANT ? { tenant: TENANT } : {}), ...Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)])) });
  return `${API_BASE}${path}?${qs.toString()}`;
};
